/**
 * useWizardSync — bidirektionale Synchronisation Wizard ↔ Supabase.
 *
 * Wird von CaseHub (und ggf. einzelnen Wizard-Steps) gemountet, um:
 *   1. den DB-Stand initial zu laden (last-write-wins gegen lokal),
 *   2. jede lokale Mutation debounced in die DB zu spiegeln.
 *
 * Crypto-Vorbedingung: ensureCryptoReady() MUSS vor dem ersten Save laufen
 * (Hook tut das beim Mount; Failure landet in errorRef und wird vom Caller
 * via UI angezeigt).
 *
 * Sicherheits-Garantie: bei Crypto-Init-Fehlschlag wird KEIN Save versucht
 * — der Hook bricht mit Status 'error' ab. UI muss in dem Zustand das
 * Wizard-Formular hard-blocken (siehe Bericht).
 */

import { useEffect, useRef, useState } from 'react';
import { useWizardStore, type WizardCaseState } from './wizard';
import { ensureCryptoReady, isEncryptedAvailable } from '../crypto';
import { createDebouncedSaver, loadWizardCase, type DebouncedSaver } from './supabaseSync';
import type { DimensionLayer } from '../supabaseTypes';

export type SyncStatus = 'idle' | 'loading' | 'ready' | 'saving' | 'error';

export interface UseWizardSyncResult {
  status: SyncStatus;
  error: string | null;
  /** Manuelles Sofort-Speichern (z.B. vor Navigation weg). */
  flush: () => Promise<void>;
}

export interface UseWizardSyncOptions {
  /** Wenn nicht gesetzt: Hook bleibt im idle (kein Auto-Sync). */
  readonly userId: string | null;
  /**
   * Ebene, in die dieser Mount schreibt. Mandant-Mount = 'mandant'
   * (Erfassung), Berater-Mount = 'berater' (fachliche Bearbeitung).
   * Default 'berater' — bestehende Aufrufer ohne Angabe verhalten sich wie
   * die Berater-Sicht. Der Load liefert immer beide (RLS-)sichtbaren Ebenen.
   */
  readonly layer?: DimensionLayer;
  readonly debounceMs?: number;
}

export function useWizardSync(caseId: string, opts: UseWizardSyncOptions): UseWizardSyncResult {
  const { userId } = opts;
  const [status, setStatus] = useState<SyncStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const saverRef = useRef<DebouncedSaver | null>(null);

  const ensureCase = useWizardStore((s) => s.ensureCase);
  const replaceCaseFromDb = useWizardStore((s) => s.replaceCaseFromDb);

  // 1. Beim Mount/User-Wechsel: Crypto initialisieren + DB-Stand laden.
  useEffect(() => {
    if (!userId) {
      setStatus('idle');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    setError(null);

    void (async () => {
      try {
        await ensureCryptoReady();
        if (!isEncryptedAvailable()) {
          throw new Error('Crypto-Schicht nicht verfügbar — Master-Key fehlt.');
        }
        const dbState = await loadWizardCase(caseId, userId);
        if (cancelled) return;

        if (dbState) {
          // last-write-wins: lokaler Stand überschreibt nur, wenn er neuer ist.
          const localState = useWizardStore.getState().cases[caseId];
          const dbTime = dbState.lastSavedAt ?? '';
          const localTime = localState?.lastSavedAt ?? '';
          if (!localState || dbTime >= localTime) {
            replaceCaseFromDb(caseId, dbState);
          } else {
            // Lokaler Stand ist neuer — er gewinnt, wird beim nächsten
            // Auto-Save in DB überschrieben. Wir loggen das bewusst NICHT
            // (kein Telemetrie-Pfad, DSGVO-Tonalität).
          }
        } else {
          // DB leer — sicherstellen, dass lokal ein Init-Case existiert.
          ensureCase(caseId);
        }
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [caseId, userId, ensureCase, replaceCaseFromDb]);

  // 2. Saver-Lifecycle: bei Status 'ready' instanziieren, bei Unmount cancel.
  useEffect(() => {
    if (status !== 'ready' || !userId) return;
    const saver = createDebouncedSaver({
      userId,
      layer: opts.layer ?? 'berater',
      delayMs: opts.debounceMs ?? 1500,
      onSaved: () => {
        setStatus('ready');
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : String(err));
        setStatus('error');
      },
    });
    saverRef.current = saver;

    // Subscribe auf Store-Mutationen für genau diesen Case.
    const unsub = useWizardStore.subscribe((s, prev) => {
      const next = s.cases[caseId];
      const old = prev.cases[caseId];
      if (!next) return;
      if (next === old) return; // identische Referenz → keine Änderung
      saver.schedule(next);
      setStatus('saving');
    });

    return () => {
      unsub();
      // Letzte gepufferte Änderung noch durchschieben, dann verwerfen.
      void saver.flush();
      saverRef.current = null;
    };
  }, [status, caseId, userId, opts.debounceMs, opts.layer]);

  return {
    status,
    error,
    flush: async () => {
      const s = saverRef.current;
      if (s) await s.flush();
    },
  };
}

/** Convenience-Selector für Smoke-Tests: aktuellen Case holen ohne Hook. */
export function getCaseSnapshot(caseId: string): WizardCaseState | undefined {
  return useWizardStore.getState().cases[caseId];
}
